import { PaymentAvailabilityService } from "src/app/services/payment-availability.service";
import { PaymentContext, PaymentGroup } from "src/app/models/payment-availability.model";
import { CommonModule, NgOptimizedImage } from "@angular/common";
import {
  Component,
  ElementRef,
  Input,
  OnInit,
  Signal,
  ViewChild,
  inject,
  signal,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NgxSpinnerService } from "ngx-spinner";
import { DropdownComponent } from "../../../components/ui-components/dropdown/dropdown.component";
import { CardButtonComponent } from "../../../components/ui-components/buttons/card-button/card-button.component";
import { phoneNumberValidator } from "../../../functions/common-functions";
import { FormInputComponent } from "../../../components/ui-components/forms/form-input/form-input.component";

import { CardButtonBackground, CardButtonSize, } from "../../../components/ui-components/buttons/card-button/card-button.model";
import { DropdownOption } from "../../../components/ui-components/dropdown/dropdown.model";
import {
  ICreateVoucherResult,
  IGetPaymentTypesResultData,
  IHeadersWithPaymentsPayment,
  IHeadersWithPaymentsResultData,
  IGetVoucherPdfResult,
  IVoucherStyle,
  IVoucherStylesResponseData,
  IInsertDertHeadersResultData,
  PopUpDialogInformalService,
  SharedService,
  StateService,
  VoucherApiService,
  IExecuteProcedureResult,
  IGetGdaHeaderResponseData,
  LoginService,
  IHeadersWithPaymentsHeader,
  isProcedureError,
  isProcedureLocalizedError,
} from "@dert-services";

import { SobitService } from "src/app/services/sobit.service";
import { PayByCardRequest } from "src/app/models/sobit-request-data";
import { IBlobExportData, IInsertHeaderRecordParams } from "src/app/services/model/sp-procedures-params.model";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { PaymentTypesService } from "src/app/services/payment-types.service";
import { VoucherTypeEnum } from "src/app/enums/voucher-type.enum";
import { FormInputType } from "../../../components/ui-components/forms/form-input/form-input.model";
import { VirtualProductType } from "src/app/enums/virtual-product-type.enum";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { BehaviorSubject, catchError, from, lastValueFrom, map, of } from "rxjs";
import { CashPaymentDialogComponent } from "src/app/components/cash-payment-dialog/cash-payment-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { BenefitPaymentDialogComponent } from "src/app/components/benefit-payment-dialog/benefit-payment-dialog.component";
import { OfflinePaymentDialogContext, PaymentDialogModel, PaymentDialogResult, ToPaymentDialogModel } from "src/app/models/payment-dialog.model";
import { IPayment, IReservationData, PaymentConditionItem } from "src/app/services/model/get-trip-data.model";
import { Router } from "@angular/router";
import { ActivatedRoute } from '@angular/router';
import { isNullOrUndefined } from "src/app/functions/common-functions";
import { TransactionService } from "src/app/services/api/transaction.service";
import { PaymentsComponent } from "src/app/components/payments/payments.component";
import { SharedModule } from "src/app/shared.module";
import { CardPaymentDialogComponent } from "src/app/components/card-payment-dialog/card-payment-dialog.component";
import { OfflineCardPaymentDialogComponent } from "src/app/components/offline-card-payment-dialog/offline-card-payment-dialog.component";
import { PaymentStatusEnum } from "src/app/enums/payment-status.enum";
import { ReportExportService } from "src/app/services/report-export.service";
import { PopUpInformalType } from "src/app/components/ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { HeaderProcessingService } from "src/app/services/header-processing.service";
import { StateDropdownService } from "src/app/services/state-dropdown.service";
import { IPaymentAttribute } from "src/app/services/model/pos.api.model";
import { VoucherReturnValueDialogComponent, VoucherReturnValueDialogModel, VoucherReturnValueDialogResult } from "src/app/components/voucher-return-value-dialog/voucher-return-value-dialog.component";
import { VoucherReturnAmountsComponent } from "src/app/components/voucher-return-amounts/voucher-return-amounts.component";
import { PaymentStatusCheckerService } from "src/app/services/payment-status-checker.service";

type VoucherPrefillDraft = {
  voucherPrice: number | null;
  voucherVisual: string | null;
  voucherType: VoucherTypeEnum | null;
  ownerName: string;
  ownerSurname: string;
  ownerAddress: string;
  ownerState: string;
  ownerCity: string;
  ownerZipCode: string;
  ownerCIN: string;
  ownerTIN: string;
  ownerEmail: string;
  ownerPhone: string;
  sendEmailToOwner: boolean;
  note: string;
};

@Component({
  providers: [PaymentAvailabilityService],
  selector: "app-sale-voucher",
  templateUrl: "./sale-voucher.component.html",
  styleUrls: ["./sale-voucher.component.scss"],
  imports: [
    CommonModule,
    TranslateModule,
    FormInputComponent,
    DropdownComponent,
    CardButtonComponent,
    ReactiveFormsModule,
    NgOptimizedImage,
    PaymentsComponent,
    FontAwesomeModule,
    SharedModule,
    VoucherReturnAmountsComponent,
],
  standalone: true,
})
export class SaleVoucherComponent implements OnInit {
  readonly paymentAvailability = inject(PaymentAvailabilityService);

  get paymentContext(): PaymentContext {
    return { operation: "sale", documentType: this.selectedVoucherType === VoucherTypeEnum.Normal ? "voucher-gift" : "voucher-benefit" };
  }

  canUsePayment(group: PaymentGroup): boolean {
    return [VoucherTypeEnum.Normal, VoucherTypeEnum.Benefit].includes(this.selectedVoucherType)
      && this.paymentAvailability.has(this.paymentContext, group);
  }

  private preparePaymentDialog(data: PaymentDialogModel): Promise<PaymentDialogModel> {
    return this.paymentAvailability.prepare(data, this.paymentContext);
  }


  private static readonly VOUCHER_CASH_SUBTYPE = "VOUCHERCASH";
  private static readonly VOUCHER_BENEFIT_SUBTYPE = "VOUCHERBENEFIT";

  private _spinner = inject(NgxSpinnerService);
  private _translate = inject(TranslateService);
  private _loginService = inject(LoginService);
  private _sharedService = inject(SharedService);
  private _sobitService = inject(SobitService);
  private _voucherService = inject(VoucherApiService);
  private _paymentTypesService = inject(PaymentTypesService);
  private _dialog = inject(MatDialog);
  private _stateService = inject(StateService);
  private _stateDropdownService = inject(StateDropdownService);
  private _router = inject(Router);
  private _route = inject(ActivatedRoute);
  private _transactionService = inject(TransactionService);
  private _popUpDialogInformalService = inject(PopUpDialogInformalService);
  private readonly _reportExportService = inject(ReportExportService);
  private readonly _headerProcessingService = inject(HeaderProcessingService);
  private readonly _paymentStatusCheckerService = inject(PaymentStatusCheckerService);

  public headerUid?: string; // Router ID, HeaderUID
  public voucherBenefitMaxPrice: number = null;
  public voucherCardMaxPrice: number = null;
  public voucherCashMaxPrice: number = null;

  public FormInputType = FormInputType;
  public CardButtonSize = CardButtonSize;
  public VoucherTypeEnum = VoucherTypeEnum;
  public CardButtonBackground = CardButtonBackground;
  public TenderType = TenderType;
  public BenefitTypes = TenderType;
  public voucherForm: FormGroup;
  public voucherStyleOptions: DropdownOption[] = [];
  public selectedVoucherStyle: IVoucherStyle | null = null;
  public selectedVoucherType: VoucherTypeEnum | null = null;
  public isCardPaymentEnabled = this._sobitService.allowPaymentByCard;
  public benefitsOptions: DropdownOption[] = [];
  public payments: BehaviorSubject<Array<IPayment>> = new BehaviorSubject([]);
  public voucherProcessing: BehaviorSubject<boolean> = new BehaviorSubject(false);

  public voucherCreateResult: BehaviorSubject<ICreateVoucherResult> = new BehaviorSubject(null);
  public voucherCode: string | null = null;
  public paymentHeaderForPrint: IInsertDertHeadersResultData | null = null;
  public processingFailed = signal(false);
  public stateOptions: DropdownOption[] = [];
  
  public voucherTypeOptions: DropdownOption[] = [
    {
      label: this._translate.instant("webPos.Voucher.Normal"),
      value: VoucherTypeEnum.Normal,
    },
    {
      label: this._translate.instant("webPos.Voucher.Benefit"),
      value: VoucherTypeEnum.Benefit,
    },
  ];

  @ViewChild("scrollContainer")
  private scrollContainer: ElementRef;
  private _loadedVoucherStyles: IVoucherStylesResponseData | null = null;
  private shouldScroll = false;
  private loadedBenefitOptions: IGetPaymentTypesResultData[] = [];

  public checkSobitPaymentEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // ---- Voucher return (CR) ----
  public isVoucherReturn = false;
  public voucherReturnSource: 'trip' | 'flyTicket' | 'eTravel' | null = null;
  public voucherReturnDocId: string | null = null;
  public voucherReturnMaxGift = 0;
  public voucherReturnMaxBenefit = 0;
  public voucherReturnVirtualProductType: VirtualProductType | null = null;
  public voucherReturnCompleted = signal(false);
  public voucherReturnVoucherHeader: IInsertDertHeadersResultData | null = null;
  public voucherReturnRefundHeader: IInsertDertHeadersResultData | null = null;
  public voucherReturnRefundHeaderId: number | null = null;
  private voucherReturnMarketCode: string | null = null;
  // -----------------------------

  async ngOnInit() {
    this.headerUid = this._route.snapshot.paramMap.get('id');
    this.isVoucherReturn = this._route.snapshot.data['type'] === 'voucherReturn';
    await this.createCustomerForm();
    if (!this.isVoucherReturn) await this.paymentAvailability.refresh();
    this._spinner.show();
    await this.loadConfiguration();
    await this.init();

    const voucherPrefill = !this.isVoucherReturn && isNullOrUndefined(this.headerUid)
      ? this.getVoucherPrefillDraft()
      : null;
    if (voucherPrefill) {
      await this.applyVoucherPrefillDraft(voucherPrefill);
      this.clearVoucherPrefillDraftFromHistory();
    }

    if (this.isVoucherReturn) {
      await this.initVoucherReturnMode();
      this._spinner.hide();
      return;
    }

    if (!isNullOrUndefined(this.headerUid)) {
      this.voucherForm.disable();
      const transDetail = await this._transactionService.getHeaderWithPayments(this.headerUid, [VirtualProductType.Voucher]);
      if (!isNullOrUndefined(transDetail)) {
        await this.loadBaseDataBlob(transDetail);
      }

      const cardPayment = transDetail?.Payments
        ?.filter(
          (p) =>
            p.PaymentType === TenderType.Card || p.PaymentType === TenderType.ElectronicVoucher
        )
        .findLast(() => true);

      if (cardPayment) {
        this.voucherForm.disable();
        if (cardPayment.Status === PaymentStatusEnum.Pending) {
          const paymentResult = await this._sobitService.payByCard({
            paymentUID: cardPayment.PaymentUID,
            valueToPay: cardPayment.PaymentValue,
            currencyIsoCode: cardPayment.CurrencyISOCode,
            isReturn: false,
            invNumber: this.voucherCode,
          } satisfies PayByCardRequest);
          if (paymentResult.outcome === "accepted") {
            const refreshedDetail = await this._transactionService.getHeaderWithPayments(this.headerUid, [VirtualProductType.Voucher]);
            if (!isNullOrUndefined(refreshedDetail)) {
              await this.loadBaseDataBlob(refreshedDetail);
            }
          }
        }
      }
      console.log(transDetail);
    }

    this._spinner.hide();
  }

  public async reloadExistingTransaction(): Promise<void> {
    if (!this.headerUid) {
      return;
    }

    const transactionDetail = await this._transactionService.getHeaderWithPayments(
      this.headerUid,
      [VirtualProductType.Voucher],
    );
    if (isNullOrUndefined(transactionDetail)) {
      return;
    }

    await this._paymentStatusCheckerService.checkAndUpdatePaymentStatusesIfPending(
      this.mapPayments(transactionDetail),
    );

    const refreshedDetail = await this._transactionService.getHeaderWithPayments(
      this.headerUid,
      [VirtualProductType.Voucher],
    );
    if (!isNullOrUndefined(refreshedDetail)) {
      await this.loadBaseDataBlob(refreshedDetail);
    }
  }

  public async startNextVoucherSale(): Promise<void> {
    await this._router.navigate(["/voucher"], {
      state: { voucherPrefill: this.buildVoucherPrefillDraft() },
    });
  }

  private buildVoucherPrefillDraft(): VoucherPrefillDraft {
    const formValue = this.voucherForm.getRawValue();

    return {
      voucherPrice: formValue.voucherPrice ?? null,
      voucherVisual: this.selectedVoucherStyle?.voucherStyle ?? formValue.voucherVisual ?? null,
      voucherType: this.selectedVoucherType ?? formValue.voucherType ?? null,
      ownerName: formValue.ownerName ?? "",
      ownerSurname: formValue.ownerSurname ?? "",
      ownerAddress: formValue.ownerAddress ?? "",
      ownerState: formValue.ownerState ?? "",
      ownerCity: formValue.ownerCity ?? "",
      ownerZipCode: formValue.ownerZipCode ?? "",
      ownerCIN: formValue.ownerCIN ?? "",
      ownerTIN: formValue.ownerTIN ?? "",
      ownerEmail: formValue.ownerEmail ?? "",
      ownerPhone: formValue.ownerPhone ?? "",
      sendEmailToOwner: !!formValue.sendEmailToOwner,
      note: formValue.note ?? "",
    };
  }

  private getVoucherPrefillDraft(): VoucherPrefillDraft | null {
    const currentNavigation = this._router.getCurrentNavigation()?.extras?.state?.["voucherPrefill"];
    const historyState = globalThis.history?.state?.voucherPrefill;
    return (currentNavigation ?? historyState ?? null) as VoucherPrefillDraft | null;
  }

  private async applyVoucherPrefillDraft(draft: VoucherPrefillDraft): Promise<void> {
    this.clearVoucherRuntimeState();
    this.voucherForm.enable();
    this.voucherForm.patchValue({
      voucherPrice: draft.voucherPrice,
      voucherVisual: draft.voucherVisual,
      voucherType: draft.voucherType,
      ownerName: draft.ownerName,
      ownerSurname: draft.ownerSurname,
      ownerAddress: draft.ownerAddress,
      ownerState: draft.ownerState,
      ownerCity: draft.ownerCity,
      ownerZipCode: draft.ownerZipCode,
      ownerCIN: draft.ownerCIN,
      ownerTIN: draft.ownerTIN,
      ownerEmail: draft.ownerEmail,
      ownerPhone: draft.ownerPhone,
      sendEmailToOwner: draft.sendEmailToOwner,
      note: draft.note,
    });
    this.selectedVoucherType = draft.voucherType;
    this.selectedVoucherStyle = this._loadedVoucherStyles?.voucherStyles.find((style) => style.voucherStyle === draft.voucherVisual)
      ?? (draft.voucherVisual ? ({ voucherStyle: draft.voucherVisual } as IVoucherStyle) : null);
    await this.updateStateOptions(draft.ownerState || undefined);
  }

  private clearVoucherRuntimeState(): void {
    this.headerUid = null;
    this.voucherCode = null;
    this.paymentHeaderForPrint = null;
    this.payments.next([]);
    this.voucherProcessing.next(false);
    this.processingFailed.set(false);
  }

  private clearVoucherPrefillDraftFromHistory(): void {
    const state = globalThis.history?.state;
    if (!state || !("voucherPrefill" in state)) {
      return;
    }

    const { voucherPrefill: _voucherPrefill, ...rest } = state as Record<string, unknown>;
    globalThis.history.replaceState(rest, document.title);
  }

  private async init(): Promise<void> {
    await this._sobitService.generateClient();
    await this.loadVoucherStyles();
    await this.loadBenefitTypes();
  }

  private async loadConfiguration(): Promise<void> {
    const loaded = await lastValueFrom(
      from(this._sharedService.loadFEConfig()).pipe(
        map(() => true),
        catchError((error) => {
          console.warn('Failed to load voucher payment configuration:', error);
          return of(false);
        })
      )
    );

    if (!loaded) {
      this.voucherBenefitMaxPrice = null;
      this.voucherCardMaxPrice = null;
      this.voucherCashMaxPrice = null;
      return;
    }

    this.voucherBenefitMaxPrice = this._stateService.configuration?.maxVoucherBenefitPrice ?? null;
    this.voucherCardMaxPrice = this._stateService.configuration?.maxVoucherCardPrice ?? null;
    this.voucherCashMaxPrice = this._stateService.configuration?.maxVoucherCashPrice ?? null;
  }

  private async updateStateOptions(ensureValue?: string): Promise<void> {
    this.stateOptions = await this._stateDropdownService.getStateOptions(ensureValue);
  }

  ngAfterViewChecked() {
    if (this.shouldScroll) {
      this.scrollToBottom();
      this.shouldScroll = false;
    }
  }

  // -------------------------- Voucher Return (CR) --------------------------

  /**
   * Loads original transaction(s) for the BNR/VS provided in route param and
   * computes max amounts that can be refunded as a Gift / Benefit voucher.
   */
  private async initVoucherReturnMode(): Promise<void> {
    const reservationId = this.headerUid;
    const source = (this._route.snapshot.queryParamMap.get('source') || 'trip') as
      | 'trip'
      | 'flyTicket'
      | 'eTravel';
    this.voucherReturnSource = source;
    this.voucherReturnDocId = reservationId;
    this.configureVoucherReturnType(source);

    this.voucherReturnVirtualProductType =
      source === 'flyTicket'
        ? VirtualProductType.FlightTicketReturn
        : source === 'eTravel'
          ? VirtualProductType.ETravelReturn
          : VirtualProductType.TripReturn;

    const types =
      source === 'trip'
        ? [VirtualProductType.Trip, VirtualProductType.TripReturn]
        : source === 'eTravel'
          ? [VirtualProductType.ETravel, VirtualProductType.ETravelReturn]
          : [VirtualProductType.FlightTicket, VirtualProductType.FlightTicketReturn];

    const reservationData = await this._transactionService.getReservationData(reservationId, types);
    if (!reservationData) {
      return;
    }

    if (isProcedureLocalizedError(reservationData)) {
      this.voucherReturnMarketCode = null;
      this.voucherReturnMaxGift = 0;
      this.voucherReturnMaxBenefit = 0;

      await this._popUpDialogInformalService.confirm({
        title: this._translate.instant("webPos.Errors.Error"),
        message: this.getLocalizedErrorMessage(reservationData.localizationCode),
        type: PopUpInformalType.Error,
      });
      return;
    }

    if (isProcedureError(reservationData)) {
      this.voucherReturnMarketCode = null;
      this.voucherReturnMaxGift = 0;
      this.voucherReturnMaxBenefit = 0;

      await this._popUpDialogInformalService.confirm({
        title: this._translate.instant("webPos.Errors.Error"),
        message: this._translate.instant("webPos.Trip.TripNotFound"),
        type: PopUpInformalType.Error,
      });
      return;
    }

    this.voucherReturnMarketCode =
      reservationData.reservation?.systemInfo?.reservationProperties
        ?.find(property => property.key === "MarketCode")
        ?.value?.trim() || null;

    // Pre-fill form from customer data — Tiger first, fall back to most recent POS payment DataBlob
    const customer = reservationData.reservation?.customer;
    const hasCustomerData = !!(customer?.firstName || customer?.lastName || customer?.emailAddress || customer?.phoneNumber);

    if (hasCustomerData) {
      const countryCode = customer.address?.countryISO3Code || undefined;
      this.voucherForm.patchValue({
        ownerName: customer.firstName ?? '',
        ownerSurname: customer.lastName ?? '',
        ownerEmail: customer.emailAddress ?? '',
        ownerPhone: customer.phoneNumber ?? '',
        ownerAddress: customer.address
          ? `${customer.address.street ?? ''} ${customer.address.streetNo ?? ''}`.trim()
          : '',
        ownerCity: customer.address?.city ?? '',
        ownerZipCode: customer.address?.zipCode ?? '',
        ownerState: countryCode ?? '',
      });
      await this.updateStateOptions(countryCode);
    } else {
      // Tiger returned no customer data — use the most recent POS payment DataBlob
      const latestBlob = (reservationData.payments ?? [])
        .filter((p) => p.DataBlob && (p.DataBlob.customerName || p.DataBlob.customerSurname || p.DataBlob.customerEmailAddress))
        .sort((a, b) => new Date(b.PaidDate).getTime() - new Date(a.PaidDate).getTime())
        .at(0)?.DataBlob;

      if (latestBlob) {
        const blobCountry = latestBlob.customerCountry || undefined;
        this.voucherForm.patchValue({
          ownerName: latestBlob.customerName ?? '',
          ownerSurname: latestBlob.customerSurname ?? '',
          ownerEmail: latestBlob.customerEmailAddress ?? '',
          ownerPhone: latestBlob.customerPhoneNumber ?? '',
          ownerAddress: latestBlob.customerStreet ?? '',
          ownerCity: latestBlob.customerCity ?? '',
          ownerZipCode: latestBlob.customerZipCode ?? '',
          ownerCIN: latestBlob.customerCIN ?? '',
          ownerTIN: latestBlob.customerTIN ?? '',
          ownerState: blobCountry ?? '',
        });
        await this.updateStateOptions(blobCountry);
      }
    }

    const paidItems = reservationData.reservation?.paymentSummary?.paymentPaidItems ?? [];

    // For flyTicket / eTravel: filter by current business unit via data.payments match
    const filteredItems =
      source !== 'trip'
        ? this.filterPaidItemsByBusinessUnit(paidItems, reservationData.payments)
        : paidItems;
    const allPaymentTypes = await this._sharedService.getAllPaymentTypes();
    const paymentsById = new Map(
      (reservationData.payments ?? []).map((payment) => [payment.Id, payment]),
    );

    let maxGift = 0;
    let maxBenefit = 0;

    if (filteredItems.length > 0) {
      // Primary: compute from Tiger paymentPaidItems (always positive amounts from Tiger)
      for (const item of filteredItems) {
        const payment = item.id ? paymentsById.get(item.id) : undefined;
        const benefitFlag = this.getPaymentBenefitFlag(allPaymentTypes, payment, item);
        if (benefitFlag === true) {
          maxBenefit += Number(item.amount) || 0;
        } else if (benefitFlag === false) {
          maxGift += Number(item.amount) || 0;
        }
      }

    } else {
      // Fallback: fly-ticket/eTravel documents can legitimately have no Tiger
      // paymentPaidItems. In that case GetTripData still returns the document's
      // completed POS payments, which must be classified by IsBenefit just like
      // the trip payment list. Keep the original synchronized-only fallback for
      // trips so their existing behavior is unchanged.
      const bu = this._loginService.user?.SelectedBusinessUnit ?? null;
      const fallbackPayments = source === 'trip'
        ? (reservationData.payments ?? []).filter(
          (p) =>
            p.PaymentType !== TenderType.Voucher &&
            this.isSynchronizedPayment(p),
        )
        : (reservationData.payments ?? []).filter(
          (p) =>
            p.Status === PaymentStatusEnum.Done &&
            Number(p.Amount) > 0 &&
            (!bu || p.BusinessUnitInternalNumber === bu || p.BusinessUnitInternalNumber == null),
        );
      for (const payment of fallbackPayments) {
        const benefitFlag = this.getPaymentBenefitFlag(allPaymentTypes, payment);
        if (benefitFlag === true) {
          maxBenefit += Number(payment.Amount) || 0;
        } else if (benefitFlag === false) {
          maxGift += Number(payment.Amount) || 0;
        }
      }
    }

    // Unsynchronized POS refunds cannot increase the limit, but must reduce it
    // immediately to prevent repeated refunds before Tiger synchronization.
    for (const payment of reservationData.payments ?? []) {
      if (
        payment.PaymentType === TenderType.Voucher
        || Number(payment.Amount) >= 0
        || payment.DataSource?.trim().toLowerCase() !== 'pos'
      ) {
        continue;
      }
      const refundedAmount = Math.abs(Number(payment.Amount));
      const benefitFlag = this.getPaymentBenefitFlag(allPaymentTypes, payment);
      if (benefitFlag === true) {
        maxBenefit -= refundedAmount;
      } else if (benefitFlag === false) {
        maxGift -= refundedAmount;
      }
    }

    // Deduct amounts already refunded via existing negative voucher return payments
    for (const payment of reservationData.payments ?? []) {
      if (payment.PaymentType !== TenderType.Voucher || Number(payment.Amount) >= 0) {
        continue;
      }
      const refundedAmount = Math.abs(Number(payment.Amount));
      if (payment.DataBlob?.voucherType === VoucherTypeEnum.Benefit) {
        maxBenefit -= refundedAmount;
      } else {
        maxGift -= refundedAmount;
      }
    }

    // For trips Tiger exposes the actual maximum refundable amount as a
    // negative remainingToPay value. Keep the tender-specific limits, but do
    // not allow either voucher type to exceed the amount still available for
    // return. Flight tickets and eTravel do not provide this value reliably.
    const remainingToPay = Number(reservationData.remainingToPay);
    if (source === 'trip' && Number.isFinite(remainingToPay)) {
      const maximumRefund = Math.max(0, -remainingToPay);
      maxGift = Math.min(maxGift, maximumRefund);
      maxBenefit = Math.min(maxBenefit, maximumRefund);
    }

    this.voucherReturnMaxGift = source === 'eTravel' ? 0 : Math.max(0, maxGift);
    this.voucherReturnMaxBenefit = Math.max(0, maxBenefit);
  }

  private configureVoucherReturnType(source: 'trip' | 'flyTicket' | 'eTravel'): void {
    if (source !== 'eTravel') {
      return;
    }

    const benefitOption = this.voucherTypeOptions.find(option => option.value === VoucherTypeEnum.Benefit);
    this.voucherTypeOptions = benefitOption ? [benefitOption] : [];
    this.selectedVoucherType = VoucherTypeEnum.Benefit;
    this.voucherForm?.get('voucherType')?.setValue(VoucherTypeEnum.Benefit, { emitEvent: false });
    this.voucherForm?.get('voucherType')?.disable({ emitEvent: false });
  }

  private getLocalizedErrorMessage(localizationCode: string): string {
    const fallbackCode = "webPos.Errors.UnexpectedError";
    const translatedMessage = this._translate.instant(localizationCode);

    if (typeof translatedMessage !== "string" || translatedMessage === localizationCode) {
      return this._translate.instant(fallbackCode);
    }

    return translatedMessage;
  }

  private isBenefitPayment(
    paymentTypes: IGetPaymentTypesResultData[],
    payment?: IPayment,
    paidItem?: PaymentConditionItem,
  ): boolean {
    return this.getPaymentBenefitFlag(paymentTypes, payment, paidItem) === true;
  }

  private isSynchronizedPayment(payment: IPayment): boolean {
    const dataSource = payment.DataSource?.trim().toLowerCase();
    return dataSource === 'tiger' || dataSource === 'both';
  }

  private getPaymentBenefitFlag(
    paymentTypes: IGetPaymentTypesResultData[],
    payment?: IPayment,
    paidItem?: PaymentConditionItem,
  ): boolean | null {
    const normalize = (value?: string | null): string => value?.trim().toUpperCase() ?? '';
    const currency = normalize(paidItem?.currency ?? payment?.Currency);
    const paymentName = normalize(payment?.PaymentName ?? paidItem?.code);

    if (paymentName) {
      const nameMatches = paymentTypes.filter(paymentType => normalize(paymentType.Name) === paymentName);
      const matchingPaymentType =
        nameMatches.find(paymentType => normalize(paymentType.Currency) === currency)
        ?? nameMatches.find(paymentType => !paymentType.Currency);
      if (matchingPaymentType) {
        return typeof matchingPaymentType.IsBenefit === 'boolean'
          ? matchingPaymentType.IsBenefit
          : null;
      }
    }

    const tenderType = Number(paidItem?.tenderType ?? payment?.PaymentType);
    if (!Number.isFinite(tenderType)) {
      return null;
    }

    const hasTenderSubType = paidItem?.tenderSubType != null;
    const tenderSubType = normalize(paidItem?.tenderSubType);
    const tenderMatches = paymentTypes.filter(
      paymentType =>
        paymentType.TenderType === tenderType
        && (!hasTenderSubType || normalize(paymentType.TenderSubType) === tenderSubType),
    );
    const exactCurrencyMatches = tenderMatches.filter(paymentType => normalize(paymentType.Currency) === currency);
    const currencyIndependentMatches = tenderMatches.filter(paymentType => !paymentType.Currency);
    const matchingPaymentTypes = exactCurrencyMatches.length > 0 ? exactCurrencyMatches : currencyIndependentMatches;

    if (matchingPaymentTypes.length !== 1) {
      return null;
    }

    return typeof matchingPaymentTypes[0].IsBenefit === 'boolean'
      ? matchingPaymentTypes[0].IsBenefit
      : null;
  }

  private filterPaidItemsByBusinessUnit(
    paidItems: PaymentConditionItem[],
    payments: IPayment[],
  ): PaymentConditionItem[] {
    const bu = this._loginService.user?.SelectedBusinessUnit ?? null;
    if (!bu) {
      return paidItems;
    }
    const buPaymentIds = new Set(
      (payments ?? [])
        .filter((p) => p.BusinessUnitInternalNumber === bu || p.BusinessUnitInternalNumber == null)
        .map((p) => p.Id),
    );
    return paidItems.filter((item) => !item.id || buPaymentIds.has(item.id));
  }

  /**
   * Opens the value dialog and orchestrates the voucher refund:
   * createVoucher -> createPaymentRecord (negative voucher payment on BNR/VS)
   * -> monitorHeader -> activateVoucher -> print receipt + voucher.
   */
  public async confirmVoucherReturn(): Promise<void> {
    if (!this.isVoucherReturn || !this.voucherReturnDocId) {
      return;
    }

    if (this.voucherReturnMaxGift <= 0 && this.voucherReturnMaxBenefit <= 0) {
      await this._popUpDialogInformalService.confirm({
        title: this._translate.instant('webPos.Errors.Error'),
        message: this._translate.instant('webPos.Voucher.NothingToRefund'),
        type: PopUpInformalType.Error,
      });
      return;
    }

    const dialogRef = this._dialog.open<
      VoucherReturnValueDialogComponent,
      VoucherReturnValueDialogModel,
      VoucherReturnValueDialogResult
    >(VoucherReturnValueDialogComponent, {
      panelClass: 'full-screen-dialog',
      data: {
        maxGift: this.voucherReturnMaxGift,
        maxBenefit: this.voucherReturnMaxBenefit,
        configMaxGift: this.voucherCashMaxPrice,
        configMaxBenefit: this.voucherBenefitMaxPrice,
        currency: this._loginService.user.CurrencyCode,
        selectedVoucherType: this.selectedVoucherType,
      },
    });

    const dialogResult = await lastValueFrom(dialogRef.afterClosed());
    if (!dialogResult) {
      return;
    }

    this.selectedVoucherType = dialogResult.voucherType;
    this.voucherForm.get('voucherType')?.setValue(dialogResult.voucherType);

    await this.executeVoucherReturn(dialogResult);
  }

  private async executeVoucherReturn(dialogResult: VoucherReturnValueDialogResult): Promise<void> {
    this.voucherProcessing.next(true);
    this.voucherForm.disable();

    const currency = this._loginService.user.CurrencyCode;

    // PaymentName must match cfg.PaymentTypes.[Name] so that sp_GetExternalTransaction
    // can JOIN it and include a TenderRecord in the GDA blob — otherwise trans.Payments
    // is never populated by ProcessTransaction.
    const paymentTypes = await this._sharedService.getPaymentTypes();
    const voucherTenderSubType = dialogResult.voucherType === VoucherTypeEnum.Benefit
      ? SaleVoucherComponent.VOUCHER_BENEFIT_SUBTYPE
      : dialogResult.voucherType === VoucherTypeEnum.Normal
        ? SaleVoucherComponent.VOUCHER_CASH_SUBTYPE
        : null;
    const voucherPaymentType = voucherTenderSubType
      ? paymentTypes.find(
        (pt) =>
          pt.TenderType === TenderType.Voucher
          && pt.TenderSubType?.toUpperCase() === voucherTenderSubType,
      )
      : null;

    if (!voucherPaymentType) {
      this.setVoucherProcessingFailure(
        `Voucher payment type configuration was not found for voucher type '${dialogResult.voucherType}'`,
      );
      return;
    }

    const voucherPaymentName = voucherPaymentType.Name;

    const dataBlob: IBlobExportData = this.createBaseDataBlob(
      dialogResult.amount,
      voucherPaymentName,
      null,
    );
    dataBlob.marketCode = this.voucherReturnMarketCode;

    const payment: IPayment = {
      Amount: dialogResult.amount,
      PaymentType: TenderType.Voucher,
      PaymentName: voucherPaymentName,
      Currency: currency,
    } as IPayment;

    const voucherResult = await this._voucherService.createVoucher(
      dataBlob,
      payment,
      { createPaymentRecord: false },
    );
    const voucherCode = this.syncVoucherCreateResult(voucherResult);
    const voucherPosHeaderUID = voucherResult?.PosHeaderUID ?? null;

    if (!voucherResult?.createdSuccess || !voucherCode || !voucherPosHeaderUID) {
      this.setVoucherProcessingFailure('Voucher creation failed for refund');
      handleVoucherCreateError(voucherResult);
      return;
    }

    // For refund flow with CreatePaymentRecord=false, paymentHeaderResult is null by design.
    // The refund header is created separately and used for voucher printing/reprint.
    this.voucherReturnVoucherHeader = null;

    // Attach voucher code to dataBlob so it appears in the payment list on the trip
    dataBlob.paymentAttributes = [{ Key: 'VoucherCode', Value: voucherCode }];

    const negativeAmount = -Math.abs(dialogResult.amount);
    // sp_GetExternalTransaction reads paymentValue from DataBlob (not from dert.payments),
    // so DataBlob must carry the negative sign for GDA to store correct return amounts.
    dataBlob.paymentValue = negativeAmount;
    dataBlob.virtualProductType = this.voucherReturnVirtualProductType;
    const refundHeaderModel: IInsertHeaderRecordParams = {
      DataBlob: dataBlob,
      VirtualProductType: this.voucherReturnVirtualProductType,
      DocID: this.voucherReturnDocId,
      PaymentType: TenderType.Voucher,
      PaymentName: voucherPaymentName,
      CurrencyISOCode: currency,
      PaymentValue: negativeAmount,
      PosHeaderUID: voucherPosHeaderUID,
    };

    const refundRecRes = await this._sharedService.createPaymentRecord(refundHeaderModel);
    const refundData = refundRecRes?.data;

    if (!refundData?.HeaderUID) {
      this.setVoucherProcessingFailure('Refund payment record was not created');
      return;
    }

    this.voucherReturnRefundHeader = refundData;
    this.paymentHeaderForPrint = refundData;
    this.payments.next([this.createPaymentFromHeader(refundData, true)]);

    const headerProcessingResult = await this._headerProcessingService.monitorHeader(
      refundData.HeaderUID,
    );
    const headerIdForPrint = this._headerProcessingService.getHeaderIdFromResult(
      headerProcessingResult,
    );
    const documentNumber = this._headerProcessingService.getDocumentNumberFromResult(
      headerProcessingResult,
    );
    this.voucherReturnRefundHeaderId = headerIdForPrint ?? null;

    if (!headerIdForPrint) {
      this.setVoucherProcessingFailure('Refund header processing failed to produce HeaderID');
      return;
    }

    if (!documentNumber) {
      this.setVoucherProcessingFailure('Refund header processing failed to produce DocumentNumber');
      return;
    }

    const activated = await this._voucherService.activateVoucher(
      voucherCode,
      documentNumber,
    );
    if (!activated) {
      this.setVoucherProcessingFailure('Voucher activation failed for refund');
      return;
    }

    this.voucherProcessing.next(false);

    if (this.voucherReturnRefundHeader) {
      await this.printVoucherDocument(this.voucherReturnRefundHeader);
    }

    await this.showVoucherReturnSuccessDialog(headerIdForPrint);

    this.voucherReturnCompleted.set(true);
  }

  private async showVoucherReturnSuccessDialog(headerIdForPrint: number | null): Promise<void> {
    const result = await this._popUpDialogInformalService.confirm({
      title: this._translate.instant('webPos.Done'),
      message: this._translate.instant('webPos.SuccessfullyReturned'),
      type: PopUpInformalType.Success,
      showPrintReceipt: headerIdForPrint != null,
      showSendReceiptByEmail: true,
      emailAddress: this.voucherForm.get('ownerEmail')?.value || '',
    });

    if (result?.printReceipt && headerIdForPrint) {
      await this._reportExportService.downloadPdf(
        undefined,
        [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
        true,
      );
    }

    if (result?.email) {
      await this._reportExportService.sendPdfByEmail(result.email, headerIdForPrint);
    }
  }

  public async printRefundReceipt(): Promise<void> {
    const headerId = this.voucherReturnRefundHeaderId ?? this.voucherReturnRefundHeader?.HeaderID;
    if (!headerId) {
      return;
    }
    await this._reportExportService.downloadPdf(
      undefined,
      [{ key: 'HeaderID', value: headerId.toString() }],
      true,
    );
  }

  public async printRefundVoucher(): Promise<void> {
    if (this.voucherReturnRefundHeader) {
      await this.printVoucherDocument(this.voucherReturnRefundHeader);
    }
  }

  public async navigateBackToSource(): Promise<void> {
    if (!this.voucherReturnDocId) {
      return;
    }
    if (this.voucherReturnSource === 'flyTicket') {
      await this._router.navigate(['/fly-ticket-return', this.voucherReturnDocId]);
    } else if (this.voucherReturnSource === 'eTravel') {
      await this._router.navigate(['/e-travel-return', this.voucherReturnDocId]);
    } else {
      await this._router.navigate(['/trip-return'], { queryParams: { bnr: this.voucherReturnDocId } });
    }
  }

  public backToSourceLabelKey(): string {
    return this.voucherReturnSource === 'flyTicket'
      ? 'webPos.Voucher.BackToFlyTicket'
      : this.voucherReturnSource === 'eTravel'
        ? 'webPos.Voucher.BackToETravel'
        : 'webPos.Voucher.BackToTrip';
  }

  // ------------------------ End Voucher Return (CR) ------------------------

  async payByCash() {
    this.checkSobitPaymentEnabled.next(false);

    const payment = await this.openCashPaymentDialog();
    if (!payment) {
      return;
    }

    const dataBlob = this.createBaseDataBlob(payment.Amount, payment.PaymentName, payment.PaymentAttributes);

    this.voucherProcessing.next(true);
    const voucherResult = await this._voucherService.createVoucher(dataBlob, payment);
    const voucherCode = this.syncVoucherCreateResult(voucherResult);
    const paymentHeader = voucherResult.paymentHeaderResult;

    if (!voucherResult.createdSuccess || !paymentHeader) {
      this.setVoucherProcessingFailure("Voucher creation failed");
      handleVoucherCreateError(voucherResult);
      return;
    }

    this.payments.next([this.createPaymentFromHeader(paymentHeader, true)]);
    this.paymentHeaderForPrint = paymentHeader;
    this.voucherForm.disable();

    const finalized = await this.finalizeVoucherAfterPrint(voucherCode, paymentHeader.HeaderUID);
    if (!finalized) {
      return;
    }

    await this.printVoucherDocument(paymentHeader);

    await this._router.navigate(['/voucher', paymentHeader.HeaderUID]);
  }

  async payByCard() {
    this.checkSobitPaymentEnabled.next(true);
    const useOfflineCardPayment = !this.isCardPaymentEnabled();

    const payment = await this.openCardPaymentDialog(useOfflineCardPayment);
    if (!payment) {
      return;
    }

    this.voucherProcessing.next(true);
    const dataBlob = {
      ...this.createBaseDataBlob(payment.Amount, payment.PaymentName, payment.PaymentAttributes),
      sendEmail: false,
    } as IBlobExportData;

    const voucherResult = await this._voucherService.createVoucher(dataBlob, payment);
    const voucherCode = this.syncVoucherCreateResult(voucherResult);
    const paymentHeader = voucherResult.paymentHeaderResult;

    if (!voucherResult.createdSuccess || !paymentHeader) {
      this.setVoucherProcessingFailure("Card voucher creation failed");
      handleVoucherCreateError(voucherResult);
      return;
    }

    const cardPaymentDefaults = await this.resolveCardPaymentDefaults(payment);
    const posHeaderUID = paymentHeader.PosHeaderUID;

    if (!posHeaderUID) {
      this.setVoucherProcessingFailure("Card voucher payment is missing PosHeaderUID");
      return;
    }

    const headerModel = {
      DataBlob: dataBlob,
      VirtualProductType: VirtualProductType.Voucher,
      DocID: voucherCode ?? voucherResult.voucherCode,
      PaymentType: payment.PaymentType || cardPaymentDefaults.paymentType,
      PaymentName: payment.PaymentName || cardPaymentDefaults.paymentName,
      CurrencyISOCode: this._loginService.user.CurrencyCode,
      PaymentValue: payment.Amount,
      PosHeaderUID: posHeaderUID,
    } as IInsertHeaderRecordParams;

    const createPaymentRecordRes = await this._sharedService.createPaymentRecord(headerModel);
    const paymentUID = createPaymentRecordRes?.data?.PaymentUID;

    if (!paymentUID) {
      this.setVoucherProcessingFailure("Card payment record was not created");
      return;
    }

    const p = createPaymentRecordRes.data;
    this.paymentHeaderForPrint = {
      ...p,
      PosHeaderUID: posHeaderUID,
    };

    if (this.isOfflineCardPayment(payment.PaymentAttributes)) {
      const finalizedOffline = await this.registerOfflineVoucherPayment(
        paymentUID,
        payment.PaymentAttributes,
        voucherCode,
        p.HeaderUID,
        posHeaderUID,
        this.paymentHeaderForPrint,
      );

      if (!finalizedOffline) {
        return;
      }

      return;
    }

    const paymentResult = await this._sobitService.payByCard({
      paymentUID,
      valueToPay: p.PaymentValue,
      currencyIsoCode: p.CurrencyISOCode,
      isReturn: false,
      showFailureDialog: false,
      invNumber: voucherCode ?? voucherResult.voucherCode,
    } satisfies PayByCardRequest);

    if (paymentResult.outcome !== "accepted") {
      if (paymentResult.outcome === "handled") {
        this.voucherProcessing.next(false);
        return;
      }

      if (useOfflineCardPayment) {
        const offlinePaymentRegistered = await this._sobitService.registerOfflineCardPayment(
          paymentUID,
          payment.PaymentAttributes,
        );

        if (!offlinePaymentRegistered) {
          this.setVoucherProcessingFailure("Offline card payment registration failed");
          return;
        }

        const finalizedOffline = await this.completeTerminalFlow({
          voucherCode,
          headerUid: p.HeaderUID,
          posHeaderUID,
          paymentHeader: this.paymentHeaderForPrint,
          emailPaymentVariable: posHeaderUID,
        });

        if (!finalizedOffline) {
          return;
        }

        await this._router.navigate(['/voucher', p.HeaderUID]);
        return;
      }

      const continueOffline = await this.confirmOfflinePaymentFallback(paymentResult.message);

      if (!continueOffline) {
        this.setVoucherProcessingFailure("Card payment cancelled after failure");
        return;
      }

      const offlinePayment = await this.openCardPaymentDialog(true, payment.Amount);

      if (!offlinePayment) {
        this.setVoucherProcessingFailure("Card payment failed");
        return;
      }

      const offlinePaymentRegistered = await this._sobitService.registerOfflineCardPayment(
        paymentUID,
        offlinePayment.PaymentAttributes,
      );

      if (!offlinePaymentRegistered) {
        this.setVoucherProcessingFailure("Offline card payment registration failed");
        return;
      }
    }

    const finalized = await this.completeTerminalFlow({
      voucherCode,
      headerUid: p.HeaderUID,
      posHeaderUID,
      paymentHeader: this.paymentHeaderForPrint,
      emailPaymentVariable: posHeaderUID,
    });
    if (!finalized) {
      return;
    }

    await this._router.navigate(['/voucher', p.HeaderUID]);
  }

  private async openCardPaymentDialog(
    offlineMode = false,
    initialAmountToPay?: number,
    offlinePaymentContext?: OfflinePaymentDialogContext | null,
  ): Promise<IPayment | null> {
    const baseData: PaymentDialogModel = await this.preparePaymentDialog({
      paymentConditionItems: [],
      maxPaymentValue: this.voucherCardMaxPrice,
      totalPaid: null,
      totalPrice: this.voucherCardMaxPrice || null,
      remainingToPay: null,
      isReturn: false,
      payments: this.payments.getValue() || [],
      initialAmountToPay: initialAmountToPay ?? null,
      offlinePaymentContext: offlinePaymentContext ?? null,
    });

    if (offlineMode) {
      const dialogRef = this._dialog.open<OfflineCardPaymentDialogComponent, PaymentDialogModel, IPayment>(
        OfflineCardPaymentDialogComponent,
        { panelClass: "full-screen-dialog", data: baseData },
      );
      return lastValueFrom(dialogRef.afterClosed());
    }

    const dialogRef = this._dialog.open<CardPaymentDialogComponent, PaymentDialogModel, PaymentDialogResult>(
      CardPaymentDialogComponent,
      { panelClass: "full-screen-dialog", data: baseData },
    );
    const result = await lastValueFrom(dialogRef.afterClosed());

    // Open the offline form from the parent to avoid stacking two full-screen
    // Material dialogs. The amount and payment metadata come from the closed form.
    if (!result?.openOfflineCardPaymentDialog) {
      return result;
    }

    return this.openCardPaymentDialog(true, result.Amount, result.offlinePaymentDialogContext);
  }

  private async openCashPaymentDialog(): Promise<IPayment | null> {
    const dialogRef = this._dialog.open<CashPaymentDialogComponent, PaymentDialogModel, IPayment>(CashPaymentDialogComponent, {
      panelClass: "full-screen-dialog",
      data: await this.preparePaymentDialog({
        paymentConditionItems: [],
        maxPaymentValue: this.voucherCashMaxPrice,
        totalPaid: null,
        totalPrice: this.voucherCashMaxPrice || null,
        remainingToPay: null,
        isReturn: false,
        payments: this.payments.getValue() || [],
      } as PaymentDialogModel),
    });

    return lastValueFrom(dialogRef.afterClosed());
  }

  private async openBenefitPaymentDialog(): Promise<IPayment | null> {
    const paymentModel = ToPaymentDialogModel({} as IReservationData);

    const dialogRef = this._dialog.open<BenefitPaymentDialogComponent, PaymentDialogModel, PaymentDialogResult>(BenefitPaymentDialogComponent, {
      panelClass: "full-screen-dialog",
      disableClose: true,
      data: await this.preparePaymentDialog({
        ...paymentModel,
        maxPaymentValue: this.voucherBenefitMaxPrice,
        totalPrice: null,
        payments: this.payments.getValue() || [],
      })
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    return this.resolveBenefitPaymentDialogResult(result);
  }

  private async resolveCardPaymentDefaults(payment: IPayment): Promise<{ paymentType: TenderType; paymentName: string }> {
    if (payment.PaymentType && payment.PaymentName) {
      return {
        paymentType: payment.PaymentType,
        paymentName: payment.PaymentName,
      };
    }

    const payments = await this._paymentTypesService.getPaymentTypesWithNominals(this.paymentContext);
    const currencyISOCode = this._loginService.user.CurrencyCode;
    const selectedPayment = payments.find((p) => p.TenderType === TenderType.Card && p.Currency === currencyISOCode);

    return {
      paymentType: selectedPayment?.TenderType ?? TenderType.Card,
      paymentName: selectedPayment?.Name ?? this._translate.instant("webPos.Card"),
    };
  }

  private syncVoucherCreateResult(voucherResult: ICreateVoucherResult): string | null {
    const voucherCode = this.normalizeVoucherCode(voucherResult);

    this.voucherCreateResult.next({
      ...voucherResult,
      voucherCode: voucherCode ?? voucherResult?.voucherCode,
      voucherNumber: voucherCode ?? voucherResult?.voucherNumber,
    });

    this.voucherCode = voucherCode ?? this.voucherCode;
    return voucherCode;
  }

  private createPaymentFromHeader(paymentHeader: IInsertDertHeadersResultData, includePaidDate = false): IPayment {
    return {
      Amount: paymentHeader.PaymentValue,
      PaymentType: paymentHeader.PaymentType,
      PaymentName: paymentHeader.PaymentName,
      Currency: paymentHeader.CurrencyISOCode,
      Id: paymentHeader.PaymentUID,
      Status: paymentHeader.Status,
      PaidDate: includePaidDate ? Date.now().toString() : undefined,
      HeaderID: paymentHeader.HeaderID,
      HeaderUID: paymentHeader.HeaderUID,
      PosHeaderUID: paymentHeader.PosHeaderUID,
    } as IPayment;
  }

  private setVoucherProcessingFailure(message: string, error?: unknown): void {
    this.voucherProcessing.next(false);
    this.voucherForm.disable();
    this.processingFailed.set(true);

    if (error) {
      console.error(message, error);
      return;
    }

    console.error(message);
  }

  private async monitorHeaderAndSetVoucherPayment(voucherCode: string, headerUid: string): Promise<number | null> {
    if (!headerUid) {
      this.setVoucherProcessingFailure("Voucher header UID is missing");
      return null;
    }

    const headerProcessingResult = await this._headerProcessingService.monitorHeader(headerUid);
    const headerIdForPrint = this._headerProcessingService.getHeaderIdFromResult(headerProcessingResult);
    const documentNumber = this._headerProcessingService.getDocumentNumberFromResult(headerProcessingResult);

    if (!documentNumber) {
      this.setVoucherProcessingFailure("Voucher document number is missing after header import");
      return null;
    }

    const voucherPaymentUpdated = await this._voucherService.setVoucherPayment(voucherCode, documentNumber);

    if (!voucherPaymentUpdated) {
      this.setVoucherProcessingFailure("Voucher payment update failed");
      return null;
    }

    return headerIdForPrint;
  }

  private async showVoucherSuccessDialog(headerIdForPrint: number | null): Promise<void> {
    if (!headerIdForPrint) {
      return;
    }

    const result = await this._popUpDialogInformalService.confirm({
      title: this._translate.instant("webPos.Done"),
      message: this._translate.instant("webPos.SuccessfullyPayed"),
      type: PopUpInformalType.Success,
      showPrintReceipt: true,
      showSendReceiptByEmail: true,
      emailAddress: this.voucherForm.get('ownerEmail')?.value || '',
    });

    if (result?.printReceipt) {
      await this._reportExportService.downloadPdf(
        undefined,
        [{ key: 'HeaderID', value: headerIdForPrint.toString() }],
        true
      );
    }

    if (result?.email) {
      await this._reportExportService.sendPdfByEmail(result.email, headerIdForPrint);
    }
  }

  private async finalizeVoucherAfterPrint(voucherCode: string | null | undefined, headerUid: string): Promise<boolean> {
    if (!voucherCode) {
      this.setVoucherProcessingFailure("Voucher code is missing after creation");
      return false;
    }

    const headerIdForPrint = await this.monitorHeaderAndSetVoucherPayment(voucherCode, headerUid);
    if (headerIdForPrint === null) {
      return false;
    }

    await this.showVoucherSuccessDialog(headerIdForPrint);
    return true;
  }

  private async completeTerminalFlow(options: {
    voucherCode: string | null | undefined;
    headerUid: string;
    posHeaderUID: string;
    paymentHeader: IInsertDertHeadersResultData;
    emailPaymentVariable: string;
  }): Promise<boolean> {
    const { voucherCode, headerUid, posHeaderUID, paymentHeader, emailPaymentVariable } = options;

    if (!voucherCode) {
      this.setVoucherProcessingFailure("Voucher code is missing after creation");
      return false;
    }

    const voucherActivationResult = await this._voucherService.activateVoucher(voucherCode, posHeaderUID);
    if (!voucherActivationResult) {
      this.setVoucherProcessingFailure("Voucher activation failed");
      return false;
    }

    const voucherEmailSent = await this.sendVoucherEmailIfRequested(emailPaymentVariable);
    if (!voucherEmailSent) {
      return false;
    }

    await this.printVoucherDocument(paymentHeader);

    const finalized = await this.finalizeVoucherAfterPrint(voucherCode, headerUid);
    if (!finalized) {
      return false;
    }

    return true;
  }

  private async sendVoucherEmailIfRequested(paymentVariable: string): Promise<boolean> {
    const shouldSendEmail = !!this.voucherForm.get('sendEmailToOwner')?.value;
    if (!shouldSendEmail) {
      return true;
    }

    const voucherSent = await this._voucherService.sendVoucher({
      PaymentVariable: paymentVariable,
      OwnerEmail: this.voucherForm.get('ownerEmail')?.value || '',
      Brand: 'dertouristik',
      SendEmail: true,
      VoucherVersion: this.selectedVoucherStyle?.voucherStyle || this.voucherForm.get('voucherVisual')?.value || '',
      VoucherPDF: {
        note: this.voucherForm.get('note')?.value || '',
      },
      Source: 'PosNG',
    });

    if (!voucherSent) {
      this.setVoucherProcessingFailure("Voucher sending failed");
      return false;
    }

    return true;
  }

  private async confirmOfflinePaymentFallback(errorMessage?: string | null): Promise<boolean> {
    const result = await this._popUpDialogInformalService.confirm({
      title: "webPos.Errors.Error",
      message: "webPos.PaymentWithCardFailed",
      subMessage: errorMessage || undefined,
      type: PopUpInformalType.Error,
      confirmButtonText: "webPos.PayOffline",
      cancelButtonText: "webPos.Cancel",
      showCancelButton: true,
    });

    return !!result?.confirmed;
  }

  private async resolveBenefitPaymentDialogResult(result: PaymentDialogResult | null): Promise<IPayment | null> {
    if (!result) {
      return null;
    }

    if (!result.openOfflineCardPaymentDialog || !result.offlinePaymentDialogContext) {
      return result;
    }

    return this.openCardPaymentDialog(
      true,
      result.Amount,
      result.offlinePaymentDialogContext,
    );
  }

  private isOfflineCardPayment(paymentAttributes?: Array<IPaymentAttribute> | null): boolean {
    return !!paymentAttributes?.some(attribute =>
      attribute?.Key === "Type"
      && attribute?.Value?.toString?.().toUpperCase() === "OFFLINE"
    );
  }

  private async registerOfflineVoucherPayment(
    paymentUID: string,
    paymentAttributes: Array<IPaymentAttribute> | null | undefined,
    voucherCode: string | null | undefined,
    headerUid: string,
    posHeaderUID: string,
    paymentHeader: IInsertDertHeadersResultData,
  ): Promise<boolean> {
    const paymentResult = await this._sobitService.registerOfflineCardPayment(
      paymentUID,
      paymentAttributes,
    );

    if (!paymentResult) {
      this.setVoucherProcessingFailure("Offline card payment registration failed");
      return false;
    }

    const finalizedOffline = await this.completeTerminalFlow({
      voucherCode,
      headerUid,
      posHeaderUID,
      paymentHeader,
      emailPaymentVariable: posHeaderUID,
    });

    if (!finalizedOffline) {
      return false;
    }

    await this._router.navigate(['/voucher', headerUid]);
    return true;
  }

  async showBenefitPaymentForm() {
    const payment = await this.openBenefitPaymentDialog();
    if (!payment) {
      return;
    }

    this.voucherProcessing.next(true);
    const dataBlob = this.createBaseDataBlob(payment.Amount, payment.PaymentName, payment.PaymentAttributes);

    const voucherResult = await this._voucherService.createVoucher(dataBlob, payment);
    const voucherCode = this.syncVoucherCreateResult(voucherResult);

    if (!voucherResult.createdSuccess) {
      this.setVoucherProcessingFailure("Benefit voucher creation failed");
      handleVoucherCreateError(voucherResult);
      return;
    }

    if (payment.PaymentType === TenderType.ElectronicVoucher) {
      const paymentHeader = voucherResult.paymentHeaderResult;

      if (!paymentHeader) {
        this.setVoucherProcessingFailure("Electronic voucher payment header is missing");
        return;
      }

      const posHeaderUID = paymentHeader.PosHeaderUID;

      if (!posHeaderUID) {
        this.setVoucherProcessingFailure("Electronic voucher payment is missing PosHeaderUID");
        return;
      }

      const headerModel = {
        DataBlob: dataBlob,
        VirtualProductType: VirtualProductType.Voucher,
        DocID: voucherCode ?? voucherResult.voucherCode,
        PaymentType: payment.PaymentType,
        PaymentName: payment.PaymentName,
        CurrencyISOCode: payment.Currency || this._loginService.user.CurrencyCode,
        PaymentValue: payment.Amount,
        PosHeaderUID: posHeaderUID,
      } as IInsertHeaderRecordParams;

      const createPaymentRecordRes = await this._sharedService.createPaymentRecord(headerModel);
      const paymentUID = createPaymentRecordRes?.data?.PaymentUID;

      if (!paymentUID) {
        this.setVoucherProcessingFailure("Electronic voucher payment record was not created");
        return;
      }

      const p = {
        ...createPaymentRecordRes.data,
        PosHeaderUID: posHeaderUID,
      };
      this.paymentHeaderForPrint = p;

      if (this.isOfflineCardPayment(payment.PaymentAttributes)) {
        const finalizedOffline = await this.registerOfflineVoucherPayment(
          paymentUID,
          payment.PaymentAttributes,
          voucherCode ?? voucherResult.voucherCode,
          p.HeaderUID,
          posHeaderUID,
          p,
        );

        if (!finalizedOffline) {
          return;
        }

        return;
      }

      const paymentResult = await this._sobitService.payByCard({
        paymentUID,
        valueToPay: p.PaymentValue,
        currencyIsoCode: p.CurrencyISOCode,
        isReturn: false,
        showFailureDialog: false,
        invNumber: voucherCode ?? voucherResult.voucherCode ?? voucherResult.voucherNumber,
      } satisfies PayByCardRequest);

      if (paymentResult.outcome !== "accepted") {
        if (paymentResult.outcome === "handled") {
          this.voucherProcessing.next(false);
          return;
        }

        const continueOffline = await this.confirmOfflinePaymentFallback(paymentResult.message);
        if (!continueOffline) {
          this.setVoucherProcessingFailure("Electronic voucher terminal payment failed");
          return;
        }

        const offlinePaymentContext = (payment as PaymentDialogResult).offlinePaymentDialogContext ?? {
          paymentType: payment.PaymentType,
          paymentName: payment.PaymentName || "",
          paymentDisplayName: payment.PaymentDescription || payment.PaymentName || "",
          currency: payment.Currency || this._loginService.user.CurrencyCode,
          paymentAttributes: payment.PaymentAttributes || [],
        };
        const offlinePayment = await this.openCardPaymentDialog(true, payment.Amount, offlinePaymentContext);
        if (!offlinePayment) {
          this.setVoucherProcessingFailure("Offline card payment cancelled");
          return;
        }

        const finalizedOffline = await this.registerOfflineVoucherPayment(
          paymentUID,
          offlinePayment.PaymentAttributes,
          voucherCode ?? voucherResult.voucherCode,
          p.HeaderUID,
          posHeaderUID,
          p,
        );
        if (!finalizedOffline) {
          return;
        }
        return;
      }

      const finalized = await this.completeTerminalFlow({
        voucherCode: voucherCode ?? voucherResult.voucherCode,
        headerUid: p.HeaderUID,
        posHeaderUID,
        paymentHeader: p,
        emailPaymentVariable: posHeaderUID,
      });
      if (!finalized) {
        return;
      }

      await this._router.navigate(['/voucher', p.HeaderUID]);
      return;
    }

    const paymentHeader = voucherResult.paymentHeaderResult;
    if (!voucherResult.activationSuccess || !paymentHeader) {
      this.setVoucherProcessingFailure("Benefit voucher activation failed");
      handleVoucherCreateError(voucherResult);
      return;
    }

    this.paymentHeaderForPrint = paymentHeader;
    this.payments.next([this.createPaymentFromHeader(paymentHeader)]);
    this.voucherForm.disable();

    await this.printVoucherDocument(paymentHeader);

    const finalized = await this.finalizeVoucherAfterPrint(voucherCode, paymentHeader.HeaderUID);
    if (!finalized) {
      return;
    }

    await this._router.navigate(['/voucher', paymentHeader.HeaderUID]);
  }


  onVoucherStyleChange(event: string) {
    const selectedStyle = event;
    this.selectedVoucherStyle = this._loadedVoucherStyles?.voucherStyles.find((style) => style.voucherStyle === selectedStyle);

    console.log("Selected voucher style:", this.selectedVoucherStyle);
  }

  private scrollToBottom(): void {
    const container = this.scrollContainer.nativeElement;
    container.scrollTop = container.scrollHeight;
  }


  onVoucherTypeChange(event: string) {
    const selectedType = event as VoucherTypeEnum;
    this.selectedVoucherType = selectedType;
    console.log("Selected voucher type:", this.selectedVoucherType);
  }

  private async loadBenefitTypes() {
    this.loadedBenefitOptions = await this._paymentTypesService.getBenefitTypesWithNominals();
    this.benefitsOptions = this._sharedService.getFormattedBenefitData(this.loadedBenefitOptions);
  }

  private async loadVoucherStyles() {
    this._loadedVoucherStyles = await this._voucherService.loadVoucherStyles();
    if (!this._loadedVoucherStyles?.voucherStyles) {
      this._sharedService.navigateToDashboard();
    }
    this.voucherStyleOptions = this._loadedVoucherStyles.voucherStyles.map(
      (style) => {
        return {
          label: style.voucherStyle,
          value: style.voucherStyle,
        };
      }
    );
  }

  private normalizeVoucherCode(result: ICreateVoucherResult | null | undefined): string | null {
    const voucherCode = result?.voucherCode?.trim() || result?.voucherNumber?.trim() || null;
    return voucherCode || null;
  }


  private async createCustomerForm() {

    this.voucherForm = new FormGroup({
      voucherPrice: new FormControl<number>(null, []),
      voucherVisual: new FormControl<string>("", [Validators.required]),
      voucherType: new FormControl<string>("", [Validators.required]),
      ownerName: new FormControl<string>("", [Validators.required]),
      ownerSurname: new FormControl<string>("", [Validators.required]),
      ownerAddress: new FormControl<string>(""),
      ownerState: new FormControl<string>(""),
      ownerCity: new FormControl<string>(""),
      ownerZipCode: new FormControl<string>(""),
      ownerCIN: new FormControl<string>(""),
      ownerTIN: new FormControl<string>(""),
      ownerEmail: new FormControl<string>("", [Validators.required, Validators.email]),
      ownerPhone: new FormControl<string>("", [Validators.required, phoneNumberValidator()]),
      sendEmailToOwner: new FormControl<boolean>(true),
      note: new FormControl<string>(""),
    });

    this.selectedVoucherStyle = null;
    this.selectedVoucherType = null;

    this.voucherForm.controls.voucherVisual.patchValue(null);
    this.voucherForm.controls.voucherType.patchValue(null);

    await this.updateStateOptions();
  }

  private createBaseDataBlob(paidValue?: number, paymentName?: string, paymentAttributes?: Array<any>): IBlobExportData {

    const formData = this.voucherForm.value;

    var price = null;
    if (!isNaN(Number(paidValue))) {
      price = Number(paidValue);
    }

    const dataBlob: IBlobExportData = {
      paymentValue: price,
      paymentName: paymentName || formData.paymentName,
      sendEmail: formData.sendEmailToOwner,
      idVoucherPayment: null,
      customerName: formData.ownerName,
      customerSurname: formData.ownerSurname,
      customerEmailAddress: formData.ownerEmail,
      customerPhoneNumber: formData.ownerPhone,
      description: formData.note,
      id: null,
      saleName: null,
      visualStyle: this.selectedVoucherStyle?.voucherStyle || null,
      customerStreet: formData.ownerAddress || null,
      customerCity: formData.ownerCity || null,
      customerCountry: formData.ownerState || null,
      customerZipCode: formData.ownerZipCode || null,
      customerCIN: formData.ownerCIN || null,
      customerTIN: formData.ownerTIN || null,
      virtualProductType: VirtualProductType.Voucher,
      virtualProductText: formData.note,
      voucherType: this.selectedVoucherType,
      paymentAttributes: paymentAttributes || null
    };
    return dataBlob;
  }

  private async loadBaseDataBlob(result: IHeadersWithPaymentsResultData) {

    const blob = result.Header.DataBlob;

    const headerDocId = result.Header?.DocID;
    const dataBlobDocId = blob?.id;

    this.voucherCode = headerDocId || dataBlobDocId || this.voucherCode;

    
    const controls = this.voucherForm.controls;
    controls.voucherPrice.setValue(blob.paymentValue);
    controls.sendEmailToOwner.setValue(blob.sendEmail);
    controls.ownerName.setValue(blob.customerName);
    controls.ownerSurname.setValue(blob.customerSurname);
    controls.ownerAddress.setValue(blob.customerStreet ?? "");
    controls.ownerState.setValue(blob.customerCountry ?? "");
    controls.ownerCity.setValue(blob.customerCity ?? "");
    controls.ownerZipCode.setValue(blob.customerZipCode ?? "");
    controls.ownerCIN.setValue(blob.customerCIN ?? "");
    controls.ownerTIN.setValue(blob.customerTIN ?? "");
    controls.ownerEmail.setValue(blob.customerEmailAddress);
    controls.ownerPhone.setValue(blob.customerPhoneNumber);
    controls.note.setValue(blob.virtualProductText);

    await this.updateStateOptions(blob.customerCountry ?? undefined);

    controls.voucherVisual.setValue(blob.visualStyle);
    this.selectedVoucherStyle = this._loadedVoucherStyles?.voucherStyles.find((style) => style.voucherStyle === blob.visualStyle);

    if (!isNullOrUndefined(result.Payments) && result.Payments.length > 0) {
      const paymentType = result.Payments[0].PaymentType;
      if (paymentType === TenderType.Cash || paymentType === TenderType.Card) {
        controls.voucherType.setValue(this.VoucherTypeEnum.Normal); 
      }
      else {
        controls.voucherType.setValue(this.VoucherTypeEnum.Benefit);  
      }
    }
    this.payments.next(this.mapPayments(result));
    this.updatePrintHeaderFromTransaction(result);
  }

  private mapPayments(result: IHeadersWithPaymentsResultData): IPayment[] {
    const headerBusinessUnit = result.Header?.BusinessUnitInternalNumber ?? null;
    return (result.Payments ?? []).map(payment => ({
      Id: payment.PaymentUID,
      Amount: payment.PaymentValue,
      PaymentType: payment.PaymentType,
      PaymentName: payment.PaymentName,
      Currency: payment.CurrencyISOCode,
      Status: payment.Status,
      PaidDate: payment.Created,
      PosPaidDate: payment.Updated,
      HeaderID: null,
      HeaderUID: result.Header?.HeaderUID ?? null,
      BusinessUnitInternalNumber: headerBusinessUnit,
      DataSource: "pos",
    } as IPayment));
  }

  private updatePrintHeaderFromTransaction(result: IHeadersWithPaymentsResultData): void {
    if (!result || !result.Header || !result.Payments?.length) {
      return;
    }

    const payment = result.Payments[result.Payments.length - 1];
    const headerData = result.Header?.DataBlob ?? {};
    const headerId =
      headerData.HeaderID ?? headerData.headerID ?? headerData.id ?? 0;

    this.paymentHeaderForPrint = {
      HeaderID: headerId,
      HeaderUID: result.Header.HeaderUID,
      PosHeaderUID: result.Header.PosHeaderUID,
      PaymentUID: payment.PaymentUID,
      PaymentType: payment.PaymentType,
      PaymentName: payment.PaymentName,
      PaymentValue: payment.PaymentValue,
      Status: payment.Status,
      CurrencyISOCode: payment.CurrencyISOCode,
    };
  }

/**
  * Requests a voucher PDF for the provided (or last known) payment header via GetVoucherPdf and opens it in a new browser window for printing.
  * @example
  * printVoucherDocument({ HeaderUID: "5DF0D095-DE9C-4FBD-BAF5-FA2E54D8B8D8", PosHeaderUID: "ABCEF..." })
  * Promise resolves to the PDF payload or null
  * @param {IInsertDertHeadersResultData} paymentHeader - Optional header record to use when building the print request; if omitted the component's paymentHeaderForPrint is used.
  * @returns {Promise<IGetVoucherPdfResult | null>} Resolves with the PDF service result when successful, or null if no header is available or an error occurs.
  * @description
  *   - Calls VoucherApiService.getVoucherPdf, which internally uses the voucher send endpoint with sendEmail=false and base64=true.
  *   - If the service returns success and a PDF payload, it delegates rendering/printing to openPdfInNewWindow.
  *   - Logs errors to the console and returns null on failure; does not throw.
  *   - The print request is prepared for immediate printing (base64 PDF) and does not send email. 
  */
  public async printVoucherDocument(
    paymentHeader?: IInsertDertHeadersResultData
  ): Promise<IGetVoucherPdfResult | null> {
    const headerToPrint = paymentHeader || this.paymentHeaderForPrint;
    if (!headerToPrint) {
      return null;
    }

    const result = await lastValueFrom(
      from(this._voucherService.getVoucherPdf(
        headerToPrint.HeaderUID,
        headerToPrint.HeaderID ?? 0,
        headerToPrint.PosHeaderUID
      )).pipe(
        catchError((error) => {
          console.error("Failed to print voucher", error);
          return of(null);
        })
      )
    );

    const voucherBase64 = result?.pdfContent ?? result?.voucherBase64;

    if (result?.success && voucherBase64) {
      this.openPdfInNewWindow(
        voucherBase64,
        "voucher.pdf",
        "application/pdf"
      );
    }

    return result;
  }

  private openPdfInNewWindow(
    base64String: string,
    fileName?: string,
    contentType: string = "application/pdf"
  ): void {
    if (!base64String) {
      return;
    }

    if (typeof window === "undefined") {
      return;
    }

    const binary = atob(base64String);
    const len = binary.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    const blob = new Blob([bytes], { type: contentType });
    const blobUrl = URL.createObjectURL(blob);
    const printWindow = window.open(blobUrl, "_blank");

    if (!printWindow) {
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      return;
    }

    const revokeUrl = () => URL.revokeObjectURL(blobUrl);

    printWindow.addEventListener("load", () => {
      if (fileName) {
        printWindow.document.title = fileName;
      }
      printWindow.focus();
      printWindow.print();
    });

    printWindow.addEventListener("afterprint", revokeUrl);
    printWindow.addEventListener("unload", revokeUrl);
  }

}


function handleVoucherCreateError(voidVoucherResult: ICreateVoucherResult) {
  const errorMessage = voidVoucherResult.error;
  alert(errorMessage);
}
